import { useMemo, useRef } from 'react';

export const useEmptyStructure = <T extends object>(objects: Array<T> | undefined): EmptyValue<T> => {
  const lastStructureRef = useRef<EmptyValue<T>>({} as EmptyValue<T>);

  return useMemo(() => {
    if (objects && objects.length > 0) {
      lastStructureRef.current = createEmptyObject(objects[0]);
    }
    // When objects is empty, return the last known structure so callers
    // can still append a correctly-shaped item to an empty list.
    return lastStructureRef.current;
  }, [objects]);
};

type EmptyValue<T> = T extends string
  ? ''
  : T extends number
    ? 0
    : T extends Array<infer U>
      ? Array<EmptyValue<U>>
      : T extends object
        ? { [K in keyof T]: EmptyValue<T[K]> }
        : T;

function createEmptyObject<T extends object>(obj: T): EmptyValue<T> {
  if (Array.isArray(obj)) {
    return [] as EmptyValue<T>;
  }

  const result: any = {};

  for (const key in obj) {
    if (Object.prototype.hasOwnProperty.call(obj, key)) {
      const value = obj[key];

      if (typeof value === 'string' || typeof value === 'number') {
        result[key] = '';
      } else if (Array.isArray(value)) {
        result[key] = [];
      } else if (typeof value === 'object' && value !== null) {
        result[key] = createEmptyObject(value);
      } else {
        result[key] = value;
      }
    }
  }

  return result as EmptyValue<T>;
}
